import { CalendarDate } from '@internationalized/date';
import type { DateRange } from 'bits-ui';

export type AvailableDateRange = { start: Date; end: Date };

function normalizeDate(date: Date): Date {
	const normalized = new Date(date);
	normalized.setHours(0, 0, 0, 0);
	return normalized;
}

function isDateWithinRanges(date: Date, ranges: AvailableDateRange[]): boolean {
	const day = normalizeDate(date).getTime();
	return ranges.some((range) => {
		const start = normalizeDate(range.start).getTime();
		const end = normalizeDate(range.end).getTime();
		return day >= start && day <= end;
	});
}

function findFirstAvailableAfter(after: Date, ranges: AvailableDateRange[]): Date | null {
	const afterNorm = normalizeDate(after);
	const maxEnd = ranges.reduce((max, range) => {
		const end = normalizeDate(range.end).getTime();
		return end > max ? end : max;
	}, afterNorm.getTime());

	const cursor = new Date(afterNorm);
	cursor.setDate(cursor.getDate() + 1);

	while (cursor.getTime() <= maxEnd) {
		if (isDateWithinRanges(cursor, ranges)) {
			return normalizeDate(cursor);
		}
		cursor.setDate(cursor.getDate() + 1);
	}

	return null;
}

export function getDefaultSingleDayDate(
	ranges: AvailableDateRange[],
	referenceDate: Date = new Date()
): Date | null {
	if (ranges.length === 0) return null;

	const today = normalizeDate(referenceDate);
	const sorted = [...ranges].sort((a, b) => a.start.getTime() - b.start.getTime());
	let candidate = normalizeDate(sorted[0].start);

	if (candidate.getTime() === today.getTime()) {
		const tomorrow = new Date(today);
		tomorrow.setDate(tomorrow.getDate() + 1);

		if (isDateWithinRanges(tomorrow, sorted)) {
			return normalizeDate(tomorrow);
		}

		return findFirstAvailableAfter(today, sorted);
	}

	return candidate;
}

export function toSingleDayDateRange(date: Date): DateRange {
	const calendarDate = new CalendarDate(date.getFullYear(), date.getMonth() + 1, date.getDate());
	return { start: calendarDate, end: calendarDate };
}
